import api from '@/lib/api';

// ---------------------------------------------------------------------------
// AxisRooms two-way sync (rate-parity read-back) — API client (E1–E7).
// All routes under /channels/sync/* (tenant-scoped, ADMIN + REVENUE_MANAGER).
// The mirror snapshot is a pure cache read; only `verify` and `push` reach
// AxisRooms server-side (verify = pull-only, push = the single explicit write).
// ---------------------------------------------------------------------------

/** D6 master-follower parity states (mirrors `channel_rate_mirror.parity_state`). */
export type ParityState =
  | 'synced'
  | 'external_change'
  | 'closed'
  | 'push_unconfirmed'
  | 'ota_divergence'
  | 'sync_failed'
  | 'unknown';

export type SyncMetric = 'rate' | 'availability';
export type VerifyStatus = 'ok' | 'couldnt_verify';

/** One mirror cell — the E1 snapshot `cells[]` shape (see rate-sync.service `toCell`). */
export interface SyncCell {
  unitId: string;
  externalListingId: string;
  rateplanId: string;
  otaId: string;
  otaName: string | null;
  date: string; // yyyy-MM-dd
  otaLoadedRate: number | null;
  otaLoadedFree: number | null;
  closed: boolean;
  pmsLastKnownRate: number | null;
  pmsLastKnownFree: number | null;
  parityState: ParityState;
  currency: string | null;
  lastSyncedAt: string | null;
  verifyStatus: VerifyStatus;
}

export interface SyncSummary {
  externalChange: number;
  pushUnconfirmed: number;
  closed: number;
  divergence: number;
  syncErrors: number;
}

export interface SyncSnapshot {
  lastSyncedAt: string | null;
  currency: string | null;
  summary: SyncSummary;
  cells: SyncCell[];
}

export interface VerifyResult {
  started: boolean;
  syncedOtas: string[];
  failedOtas: string[];
  summary: SyncSummary;
}

/** One append-only change-log row (E3 / E4 history). */
export interface ChangeLogRow {
  id: string;
  propertyId: string;
  channelConnectionId: string;
  unitId: string;
  externalListingId: string | null;
  rateplanId: string | null;
  otaId: string | null;
  otaName: string | null;
  stayDate: string;
  metric: SyncMetric;
  eventType:
    | 'external_change'
    | 'push'
    | 'push_unconfirmed'
    | 'push_confirmed'
    | 'closed'
    | 'reopened'
    | 'sync_failed';
  oldValue: string | null;
  newValue: string | null;
  currency: string | null;
  actor: string | null;
  raw: Record<string, unknown> | null;
  detectedAt: string;
}

export interface PaginatedChangeLog {
  data: ChangeLogRow[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface CellDetail {
  cells: SyncCell[];
  history: ChangeLogRow[];
}

export interface StatusPill {
  externalChange: number;
  pushUnconfirmed: number;
  syncErrors: number;
  lastSyncedAt: string | null;
  stale: boolean;
}

export interface PushCellResult {
  result: unknown[];
  pending: boolean;
  wouldOverwriteExternalChange: boolean;
}

// ─── Query shapes ─────────────────────────────────────────────────────────

export interface SyncSnapshotQuery {
  propertyId: string;
  from?: string;
  to?: string;
  metric?: SyncMetric;
  otaId?: string;
  roomId?: string;
}

export interface VerifySyncBody {
  propertyId: string;
  otaId?: string;
  roomId?: string;
  from?: string;
  to?: string;
}

export interface ChangeLogQuery {
  propertyId: string;
  otaId?: string;
  roomId?: string;
  state?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface CellDetailQuery {
  propertyId: string;
  unitId: string;
  rateplanId: string;
  date: string;
}

export interface PushSyncCellBody {
  propertyId: string;
  unitId: string;
  rateplanId: string;
  metric: SyncMetric;
  from: string;
  to: string;
  value: number;
}

export interface SyncExportQuery {
  propertyId: string;
  from?: string;
  to?: string;
  metric?: SyncMetric;
  otaId?: string;
  roomId?: string;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

/** E1 — GET /channels/sync/snapshot. */
export async function fetchSyncSnapshot(q: SyncSnapshotQuery): Promise<SyncSnapshot> {
  const { data } = await api.get<SyncSnapshot>('/channels/sync/snapshot', { params: q });
  return data;
}

/** E2 — POST /channels/sync/verify (PULL-ONLY). */
export async function verifySync(body: VerifySyncBody): Promise<VerifyResult> {
  const { data } = await api.post<VerifyResult>('/channels/sync/verify', body);
  return data;
}

/** E3 — GET /channels/sync/change-log. */
export async function fetchChangeLog(q: ChangeLogQuery): Promise<PaginatedChangeLog> {
  const { data } = await api.get<PaginatedChangeLog>('/channels/sync/change-log', { params: q });
  return data;
}

/** E4 — GET /channels/sync/cell. */
export async function fetchCellDetail(q: CellDetailQuery): Promise<CellDetail> {
  const { data } = await api.get<CellDetail>('/channels/sync/cell', { params: q });
  return data;
}

/** E5 — POST /channels/sync/push (the ONLY AxisRooms-write here). */
export async function pushSyncCell(body: PushSyncCellBody): Promise<PushCellResult> {
  const { data } = await api.post<PushCellResult>('/channels/sync/push', body);
  return data;
}

/** E6 — GET /channels/sync/status-pill. */
export async function fetchStatusPill(propertyId: string): Promise<StatusPill> {
  const { data } = await api.get<StatusPill>('/channels/sync/status-pill', {
    params: { propertyId },
  });
  return data;
}

/**
 * E7 — GET /channels/sync/export (CSV stream). Fetched as a blob through the
 * authenticated axios instance (the export route is Bearer-guarded, so a bare
 * `<a download>` would 401), then handed back for a client-side download.
 */
export async function fetchSyncExport(q: SyncExportQuery): Promise<Blob> {
  const { data } = await api.get('/channels/sync/export', {
    params: q,
    responseType: 'blob',
  });
  return data as Blob;
}

/** Trigger a browser download of the exported CSV. */
export async function downloadSyncExport(q: SyncExportQuery): Promise<void> {
  const blob = await fetchSyncExport(q);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `axisrooms-sync-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has committed.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Primary-OTA preference (Q-A) ────────────────────────────────────────────
//
// The single Rate-Calendar cell mirrors one "primary" OTA. The backend resolves
// this server-side from `channel_connections.primary_ota_id` (fallback: the
// smallest ota_id). The existing UpdateConnectionDto does NOT carry
// `primary_ota_id`, so — to stay strictly frontend + no-regression — the Sync
// tab's picker persists the revenue user's choice as a per-viewer preference and
// uses it to pick which OTA the calendar overlay / sync grid treats as primary.
// It degrades gracefully to the smallest ota_id when unset or storage is blocked.

const PRIMARY_OTA_KEY = 'pms.sync.primaryOta';

export function getPrimaryOtaPref(propertyId: string): string | null {
  try {
    const raw = localStorage.getItem(PRIMARY_OTA_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, string>;
    return map[propertyId] ?? null;
  } catch {
    return null;
  }
}

export function setPrimaryOtaPref(propertyId: string, otaId: string): void {
  try {
    const raw = localStorage.getItem(PRIMARY_OTA_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[propertyId] = otaId;
    localStorage.setItem(PRIMARY_OTA_KEY, JSON.stringify(map));
  } catch {
    // per-viewer convenience only — safe to drop
  }
}

/**
 * Distinct connected OTAs present in a snapshot, ascending by id (the backend's
 * deterministic default order). Powers the primary-OTA picker + per-OTA grouping.
 */
export function connectedOtasFromSnapshot(
  cells: SyncCell[],
): Array<{ otaId: string; otaName: string }> {
  const seen = new Map<string, string>();
  for (const c of cells) {
    if (!seen.has(c.otaId)) seen.set(c.otaId, c.otaName ?? c.otaId);
  }
  return [...seen.entries()]
    .map(([otaId, otaName]) => ({ otaId, otaName }))
    .sort((a, b) => a.otaId.localeCompare(b.otaId));
}

/** Resolve the effective primary OTA id for a property from its snapshot. */
export function resolvePrimaryOta(propertyId: string, cells: SyncCell[]): string | null {
  const otas = connectedOtasFromSnapshot(cells);
  if (otas.length === 0) return null;
  const pref = getPrimaryOtaPref(propertyId);
  if (pref && otas.some((o) => o.otaId === pref)) return pref;
  return otas[0]!.otaId; // smallest ota_id — matches backend fallback
}
